/// <amd-module name="Wit/Common/BulkUpdateUtils/BulkUpdateUtils" />
import { IVssPageContext } from "VSS/Platform/Context";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { processTagsChanges } from "Wit/Common/BulkUpdateUtils/TagUtils";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { CoreField, CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { INamedFieldDataDictionary, IProjectDataWithNodes } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { isEditable } from "Wit/WorkItemModel/WorkItem/FieldUtils";

/**
 * Given a list of work item ids or a list of work item objects and a mapping of work item types to field changes, return a list of WorkItem objects containing
 * the field changes
 */
export async function performBulkUpdate(
    pageContext: IVssPageContext,
    workItemsToUpdate: number[] | IWorkItem[],
    changesMap: Map<string, IWorkItemFieldChange[]>
): Promise<IWorkItem[]> {
    if (!workItemsToUpdate.length) {
        return [];
    }

    const workItemService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");

    // if a list of ids was supplied, we'll load the work item objects
    const workItems = isIntegerList(workItemsToUpdate) ? await workItemService.getWorkItems(workItemsToUpdate) : (workItemsToUpdate as IWorkItem[]);
    const workItemPromises = [];
    for (const workItem of workItems) {
        const workItemType = workItem.getFieldValueById(CoreField.WorkItemType);
        const changes = changesMap.get(workItemType) ?? changesMap.get(BulkUpdateConstants.AllTypes);
        if (!changes) {
            console.warn(`Couldn't find change list for work item ${workItem.id} with work item type ${workItemType}`);
            continue;
        }

        workItemPromises.push(updateWorkItemFields(pageContext, workItem, changes));
    }

    await Promise.all(workItemPromises);
    return workItems;
}

async function updateWorkItemFields(pageContext: IVssPageContext, workItem: IWorkItem, changes: IWorkItemFieldChange[]) {
    for (const change of changes) {
        const field = workItem.getFieldByName(change.fieldName);
        if (field) {
            // Bulk work item type change
            if (field.fieldDefinition.id === CoreField.WorkItemType) {
                let project = workItem.project.guid;
                let projectDataWithNodes: IProjectDataWithNodes | undefined = undefined;
                let destinationProjectFields: IFieldEntry[] | undefined = undefined;
                const projectChange = changes.find(c => c.fieldName === CoreFieldRefNames.TeamProject);
                if (projectChange) {
                    const dataManager = pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
                    const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
                    const projects = await dataManager.beginGetFieldProjectData(projectChange.value);
                    const areaNodes = await nodeService.getAreaPathNodes(projectChange.value);
                    const iterationNodes = await nodeService.getIterationNodes(projectChange.value);
                    const projectData = projects?.projects.find(p => p.guid === projectChange.value);
                    if (!projectData || !areaNodes || !iterationNodes) {
                        console.warn(
                            `Failed to find project data for project ${project}, aborting update of work item type for work item ${workItem.id}`
                        );
                        continue;
                    }

                    destinationProjectFields = projects?.fields;
                    project = projectData.guid;
                    projectDataWithNodes = {
                        ...projectData,
                        areaNodes,
                        iterationNodes
                    };
                }

                const workItemTypesService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
                const typeData = await workItemTypesService.getWorkItemTypeData(project, [change.value]);
                workItem.changeWorkItemType(typeData[0], projectDataWithNodes, [], destinationProjectFields);
            } else {
                if (isEditable(field)) {
                    // If we are updating the History field, prepend our text
                    // so we don't kill off any transient text added since the last change.
                    let newFieldValue = change.value;
                    if (field.fieldDefinition.id === CoreField.History) {
                        // Only set history/discussion as the current value if it is dirty
                        const previousValue = workItem.isFieldDirty(CoreField.History) ? workItem.getFieldValueById(CoreField.History) : "";
                        newFieldValue = `${change.value}${!previousValue ? "" : "<BR>" + previousValue}`;
                    } else if (field.fieldDefinition.type === FieldType.DateTime && change.value) {
                        newFieldValue = change.value instanceof Date ? (change.value as Date) : new Date(change.value).toLocaleString();
                    }

                    workItem.setFieldValueById(field.fieldDefinition.id, newFieldValue, { handleExceptions: true });
                }
            }
        } else {
            processTagsChanges(workItem, change);
        }
    }
}

export function getWorkItemTypes(workItemData: INamedFieldDataDictionary[]): string[] {
    const types = new Set<string>();
    for (const workItem of workItemData) {
        types.add(workItem[CoreFieldRefNames.WorkItemType]);
    }

    return [...types.values()];
}

function isIntegerList(list: any[]): list is number[] {
    return list.length > 0 && typeof list[0] === "number";
}
